import { Box, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { useAppDispatch, useAppSelector } from "@common-reducers/hooks/store.hook";
import { addScheduledMessageButtonActions, selectScheduleRecipients } from "@common-reducers/AddScheduledMessageButtonReducer";
import { setAddMsgInCalendarContact } from "@common-reducers/WhatsAppReducer";
import { STATUS_AUDIENCE_LABEL, STATUS_RECIPIENT, hasStatusRecipient } from "@common/scheduling/status-recipient";
import { namespace } from "@common-config/constants";
import { setCancelIfReceived, setIsPoll } from "@common/reducers/ScheduledMessageEditorReducer";

// FE#335 — the compose DESTINATION picker: "Schedule a [ Message | Status ]".
//
// This replaced a bare text link + a lone MUI Chip. That older shape had no
// visible notion of two modes (the only way back from Status was a tiny × on a
// chip), and it leaned on a 📢 emoji while the rest of the dialog uses line
// icons. A segmented control states the choice up front and makes the return
// trip obvious.
//
// "Message" is the DEFAULT and covers the normal chats/groups flow — picking it
// simply clears the Status recipient so the contact picker comes back. Status is
// mutually exclusive with chats/groups (enforced in the reducer by
// setStatusRecipient / applyAddRecipient), so `send-message-to` hides the
// contact picker while it is active.
//
// MVP posts to the account's DEFAULT Status audience — surfaced explicitly under
// the toggle so reach is never a surprise. The audience selector is Phase 2.
export const StatusRecipientToggle = () => {
  const { t } = useTranslation(namespace);
  const dispatch = useAppDispatch();
  const recipients = useAppSelector(selectScheduleRecipients);
  const statusSelected = hasStatusRecipient(recipients);

  const selectStatus = () => {
    if (statusSelected) return;
    dispatch(addScheduledMessageButtonActions.setStatusRecipient());
    dispatch(setAddMsgInCalendarContact(STATUS_RECIPIENT as never));
    // A Status is never a poll and has no reply to cancel on: drop both if they were set.
    dispatch(setIsPoll(false));
    dispatch(setCancelIfReceived(false));
  };

  // Back to the normal flow — drop the Status recipient so the contact picker
  // re-appears. Deliberately a no-op when Status was never selected, so a stray
  // click on the already-active "Message" tab can't wipe chosen recipients.
  const selectMessage = () => {
    if (!statusSelected) return;
    dispatch(addScheduledMessageButtonActions.setRecipients([]));
    dispatch(setAddMsgInCalendarContact(null));
  };

  // Compact on purpose: the destination toggle is a small mode switch, not a
  // primary action — it must not compete with the editor or the Schedule button.
  // Tabs size to their own content (no flex:1) so the control stays narrow.
  const tabSx = (active: boolean) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 0.5,
    py: 0.25,
    px: 1.25,
    borderRadius: "999px",
    cursor: "pointer",
    fontSize: 12.5,
    lineHeight: 1.6,
    fontWeight: 600,
    whiteSpace: "nowrap" as const,
    userSelect: "none" as const,
    color: active ? "text.primary" : "text.secondary",
    bgcolor: active ? "background.paper" : "transparent",
    boxShadow: active ? "0 1px 3px rgba(0,0,0,.18)" : "none",
    transition: "all .15s ease",
  });

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, width: "100%" }}>
      <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
        <Typography variant="caption" sx={{ color: "text.secondary", fontSize: 12.5 }}>
          {t("messageScheduling.scheduleADestination")}
        </Typography>
        <Box
          role="tablist"
          aria-label={t('messageScheduling.destinationAria', 'Schedule destination')}
          sx={{ display: "flex", gap: 0.25, p: 0.375, borderRadius: "999px", bgcolor: "action.hover" }}
        >
          <Box
            role="tab"
            aria-selected={!statusSelected}
            onClick={selectMessage}
            sx={tabSx(!statusSelected)}
          >
            <ChatBubbleOutlineIcon sx={{ fontSize: 15 }} /> {t("messageScheduling.destinationMessage")}
          </Box>
          <Box
            role="tab"
            aria-selected={statusSelected}
            onClick={selectStatus}
            sx={tabSx(statusSelected)}
          >
            <RadioButtonUncheckedIcon sx={{ fontSize: 15 }} /> {t("messageScheduling.destinationStatus")}
          </Box>
        </Box>
      </Stack>

      {statusSelected && (
        <Typography variant="caption" sx={{ opacity: 0.7 }}>
          {t("messageScheduling.statusAudience", STATUS_AUDIENCE_LABEL)}
        </Typography>
      )}
    </Box>
  );
};
